import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/**
 * Every column the payout report CSV can carry. The merchant picks a subset in
 * their payments settings; the public v3 `payoutReport` field accepts the same
 * keys. Column order in the file follows `HSA_PAYOUT_REPORT_FIELD_CATALOG`,
 * never the merchant's click order.
 *
 * Deliberately absent: customer name and email (shopper identity tied to an
 * HSA purchase; the v3 payments shapes already drop the email), the provider's
 * `payout_reference` (an Adyen transfer id, support correlation only),
 * `counterparty` (bank details), and `environment`.
 */
export enum HsaPayoutReportField {
  PayoutId = "payout_id",
  PayoutStatus = "payout_status",
  PayoutType = "payout_type",
  PayoutAmount = "payout_amount",
  Currency = "currency",
  ValueDate = "value_date",
  CreatedAt = "created_at",
  EstimatedArrivalDate = "estimated_arrival_date",
  ConfirmedAt = "confirmed_at",
  MerchantReference = "merchant_reference",
  OrderReference = "order_reference",
  LineTypes = "line_types",
  NetInThisPayout = "net_in_this_payout",
  OrderGross = "order_gross",
  OrderPlatformFee = "order_platform_fee",
  OrderLmnFee = "order_lmn_fee",
  OrderTotalFees = "order_total_fees",
}

export enum HsaPayoutReportFieldGroup {
  /** Repeats on every row of the file. */
  Payout = "payout",
  /** One value per order line. */
  Order = "order",
}

export interface HsaPayoutReportFieldDefinition {
  key: HsaPayoutReportField;
  label: string;
  group: HsaPayoutReportFieldGroup;
  description: string;
}

/** The catalog in file column order. */
export const HSA_PAYOUT_REPORT_FIELD_CATALOG: readonly HsaPayoutReportFieldDefinition[] =
  [
    {
      key: HsaPayoutReportField.PayoutId,
      label: "Payout ID",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "The payout's id, as shown on the Payouts page.",
    },
    {
      key: HsaPayoutReportField.PayoutStatus,
      label: "Payout status",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "paid, pending, returned, failed, or cancelled.",
    },
    {
      key: HsaPayoutReportField.PayoutType,
      label: "Payout type",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "payout for a deposit; payout_return for a reversal.",
    },
    {
      key: HsaPayoutReportField.PayoutAmount,
      label: "Payout amount",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "Total deposited, as a decimal in the payout's currency.",
    },
    {
      key: HsaPayoutReportField.Currency,
      label: "Currency",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "ISO 4217 currency code of every amount in the file.",
    },
    {
      key: HsaPayoutReportField.ValueDate,
      label: "Value date",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "Banking date the deposit is valued (YYYY-MM-DD).",
    },
    {
      key: HsaPayoutReportField.CreatedAt,
      label: "Created at",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "When the payout was created (UTC).",
    },
    {
      key: HsaPayoutReportField.EstimatedArrivalDate,
      label: "Estimated arrival date",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "Estimated date the funds reach the bank (YYYY-MM-DD).",
    },
    {
      key: HsaPayoutReportField.ConfirmedAt,
      label: "Confirmed at",
      group: HsaPayoutReportFieldGroup.Payout,
      description: "When the payout report confirmed the deposit (UTC).",
    },
    {
      key: HsaPayoutReportField.MerchantReference,
      label: "Merchant reference",
      group: HsaPayoutReportFieldGroup.Order,
      description: "Your own reference for the order, when one was recorded.",
    },
    {
      key: HsaPayoutReportField.OrderReference,
      label: "Order reference",
      group: HsaPayoutReportFieldGroup.Order,
      description:
        "The order reference; empty when a line could not be matched to an order.",
    },
    {
      key: HsaPayoutReportField.LineTypes,
      label: "Line types",
      group: HsaPayoutReportFieldGroup.Order,
      description: "capture, refund, or both separated by |.",
    },
    {
      key: HsaPayoutReportField.NetInThisPayout,
      label: "Net in this payout",
      group: HsaPayoutReportFieldGroup.Order,
      description:
        "This order's contribution to the payout; negative for a refund-only line.",
    },
    {
      key: HsaPayoutReportField.OrderGross,
      label: "Order gross",
      group: HsaPayoutReportFieldGroup.Order,
      description:
        "The whole order's gross amount before fees; repeats on every line for that order.",
    },
    {
      key: HsaPayoutReportField.OrderPlatformFee,
      label: "Order platform fee",
      group: HsaPayoutReportFieldGroup.Order,
      description: "Redo's platform commission on the whole order.",
    },
    {
      key: HsaPayoutReportField.OrderLmnFee,
      label: "Order LMN fee",
      group: HsaPayoutReportFieldGroup.Order,
      description: "Letter-of-medical-necessity fee on the whole order.",
    },
    {
      key: HsaPayoutReportField.OrderTotalFees,
      label: "Order total fees",
      group: HsaPayoutReportFieldGroup.Order,
      description: "Platform fee plus LMN fee on the whole order.",
    },
  ];

/** The starting selection, in catalog order. */
export const HSA_PAYOUT_REPORT_DEFAULT_FIELDS: readonly HsaPayoutReportField[] =
  [
    HsaPayoutReportField.PayoutId,
    HsaPayoutReportField.PayoutAmount,
    HsaPayoutReportField.Currency,
    HsaPayoutReportField.ValueDate,
    HsaPayoutReportField.MerchantReference,
    HsaPayoutReportField.OrderReference,
    HsaPayoutReportField.LineTypes,
    HsaPayoutReportField.NetInThisPayout,
    HsaPayoutReportField.OrderGross,
    HsaPayoutReportField.OrderPlatformFee,
    HsaPayoutReportField.OrderLmnFee,
    HsaPayoutReportField.OrderTotalFees,
  ];

export const HSA_PAYOUT_REPORT_MAX_RECIPIENTS = 10;

/** RFC 5321 caps a mailbox path at 254 octets; anything longer is not an
 * address anyone can receive at. */
export const HSA_PAYOUT_REPORT_MAX_RECIPIENT_LENGTH = 254;

export const hsaPayoutReportFieldSchema = createEnum(HsaPayoutReportField);

export const hsaPayoutReportRecipientSchema = z
  .email()
  .max(HSA_PAYOUT_REPORT_MAX_RECIPIENT_LENGTH);

export const hsaPayoutReportSettingsSchema = z.object({
  /** The merchant's switch. Off keeps the recipient list; the report sends
   * only when this is on and at least one recipient is listed. */
  enabled: z.boolean(),
  recipients: z
    .array(hsaPayoutReportRecipientSchema)
    .max(HSA_PAYOUT_REPORT_MAX_RECIPIENTS),
  fields: z.array(hsaPayoutReportFieldSchema).min(1),
});
export type HsaPayoutReportSettings = z.infer<
  typeof hsaPayoutReportSettingsSchema
>;

/**
 * Trims, lowercases, drops blanks, and removes duplicates while keeping the
 * first occurrence's position. Addresses are compared case-insensitively as a
 * whole, since no mail provider Redo sends to distinguishes local-part case.
 */
export function normalizeHsaPayoutReportRecipients(
  raw: readonly string[],
): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const value of raw) {
    const address = value.trim().toLowerCase();
    if (address.length === 0 || seen.has(address)) {
      continue;
    }
    seen.add(address);
    normalized.push(address);
  }
  return normalized;
}

/** Removes duplicates and puts the selection into catalog column order. */
export function orderHsaPayoutReportFields(
  fields: readonly HsaPayoutReportField[],
): HsaPayoutReportField[] {
  const selected = new Set(fields);
  return HSA_PAYOUT_REPORT_FIELD_CATALOG.map((field) => field.key).filter(
    (key) => selected.has(key),
  );
}
